import { RefreshCw } from 'lucide-react'
import { assetPath } from '@/utils/assetPath'
import { Button } from './atoms/Button'
import HomeIcon from '@/icons/home'

interface ErrorFallbackUIProps {
    error?: Error | null
    errorInfo?: { componentStack?: string }
    onRetry?: () => void
    onGoHome?: () => void
    onGoBack?: () => void
}

export function ErrorFallbackUI({
    error,
    errorInfo,
    onRetry = () => window.location.reload(),
    onGoHome = () => (window.location.href = import.meta.env.BASE_URL),
    onGoBack = () => window.history.back(),
}: ErrorFallbackUIProps) {
    const isError = error instanceof Error
    const errorMessage = isError
        ? error.message
        : 'An unexpected error occurred'
    const errorStack = isError ? error.stack : undefined
    const errorName = isError ? error.name : undefined
    const componentStack = errorInfo?.componentStack

    return (
        <div className="mx-auto flex min-dvh max-w-md flex-col bg-white p-6">
            {/* Back Button */}
            <div className="mb-4">
                <button
                    onClick={onGoBack}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 18L9 12L15 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="font-medium">Back</span>
                </button>
            </div>

            {/* Logo */}
            <div className="mb-8 flex justify-center">
                <img
                    src={assetPath('logo/logo.svg')}
                    alt="what the fuck are you wearing"
                    className="h-12 w-auto"
                />
            </div>

            {/* Main Content - Centered */}
            <div className="flex flex-1 flex-col items-center justify-center text-center">
                {/* GIF in rounded black container */}
                <div className="mb-8 w-full max-w-sm overflow-hidden rounded-3xl bg-black">
                    <img
                        src="https://r2.wtf.style/assets/videos/9642c3ca-6da9-4f54-8214-5f4b1bd25ab1.gif"
                        alt="Error occurred"
                        className="h-auto w-full"
                    />
                </div>

                {/* Error Title */}
                <h2 className="mb-4 text-2xl font-bold text-gray-900">
                    Oops! Something went wrong
                </h2>

                {/* Error Message */}
                <p className="mb-8 text-gray-600">
                    We have noted this error and will fix for future use
                </p>

                {/* Action Buttons */}
                <div className="flex w-full max-w-xs gap-3">
                    <Button
                        onClick={onRetry}
                        className="rounded-xl bg-black px-6 py-3 font-medium text-white"
                        fullWidth
                        leftIcon={<RefreshCw size={20} />}
                    >
                        Try Again
                    </Button>

                    <Button
                        onClick={onGoHome}
                        variant="outline"
                        className="rounded-xl border-gray-300 px-6 py-3 font-medium text-gray-700"
                        fullWidth
                        leftIcon={
                            <HomeIcon
                                width={20}
                                height={20}
                                fill="currentColor"
                            />
                        }
                    >
                        Home
                    </Button>
                </div>

                {/* Dev Mode Error Details */}
                {import.meta.env.DEV && error && (
                    <div className="mt-8 w-full max-w-md">
                        <details className="text-left">
                            <summary className="mb-2 cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                Show technical details (Dev Mode)
                            </summary>
                            <div className="max-w-full overflow-hidden rounded-lg border bg-gray-50 p-3 font-mono text-xs text-gray-700">
                                <div className="mb-2 wrap-break-words">
                                    <strong>Error:</strong> {errorMessage}
                                </div>
                                {errorName && (
                                    <div className="mb-2 wrap-break-words">
                                        <strong>Type:</strong> {errorName}
                                    </div>
                                )}
                                {errorStack && (
                                    <div className="wrap-break-words">
                                        <strong>Stack trace:</strong>
                                        <pre className="mt-1 overflow-hidden text-xs break-all whitespace-pre-wrap">
                                            {errorStack}
                                        </pre>
                                    </div>
                                )}
                                {componentStack && (
                                    <div className="wrap-break-words">
                                        <strong>Component Stack:</strong>
                                        <pre className="mt-1 overflow-hidden text-xs break-all whitespace-pre-wrap">
                                            {componentStack}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </details>
                    </div>
                )}
            </div>
        </div>
    )
}
